/**
 * Hero Section Component
 *
 * Homepage hero section with gradient background, headline, description, and CTAs.
 * Built with Lightning Velocity design tokens.
 */

import React from 'react';
import { clsx } from 'clsx';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';

export interface HeroProps {
  /**
   * Optional badge/label above headline
   */
  badge?: string;

  /**
   * Main headline
   */
  headline: string;

  /**
   * Supporting description
   */
  description: string;

  /**
   * Primary CTA button text
   */
  primaryCta: string;

  /**
   * Primary CTA button click handler
   */
  onPrimaryCta?: () => void;

  /**
   * Secondary CTA button text (optional)
   */
  secondaryCta?: string;

  /**
   * Secondary CTA button click handler
   */
  onSecondaryCta?: () => void;

  /**
   * Optional image/illustration
   */
  image?: React.ReactNode;

  /**
   * Background variant
   */
  background?: 'gradient' | 'solid' | 'subtle';
}

/**
 * Hero section using Lightning Velocity design system
 */
export const Hero: React.FC<HeroProps> = ({
  badge,
  headline,
  description,
  primaryCta,
  onPrimaryCta,
  secondaryCta,
  onSecondaryCta,
  image,
  background = 'gradient',
}) => {
  // Background styles
  const backgroundStyles = {
    gradient: 'bg-gradient-to-br from-brand-primary via-brand-primary-dark to-brand-accent',
    solid: 'bg-brand-primary',
    subtle: 'bg-neutral-50',
  };

  // Text color based on background
  const textColor = background === 'subtle'
    ? 'text-neutral-900'
    : 'text-white';

  const descriptionColor = background === 'subtle'
    ? 'text-neutral-600'
    : 'text-white/90';

  return (
    <section className={clsx(
      'relative overflow-hidden',
      backgroundStyles[background],
      // Mobile gets a tighter hero so the primary CTA stays above the fold on
      // small phones (iPhone SE inside FB in-app has only ~580px of usable
      // height). Desktop padding unchanged.
      'pt-8 pb-10 md:py-32'
    )}>
      {/* Decorative background elements */}
      {background !== 'subtle' && (
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>
      )}

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-4 md:space-y-8">
            {/* Badge */}
            {badge && (
              <div className="animate-fade-in">
                <Badge variant={background === 'subtle' ? 'primary' : 'secondary'} size="md">
                  {badge}
                </Badge>
              </div>
            )}

            {/* Headline — scaled down on mobile so the CTA stays in view */}
            <h1 className={clsx(
              'text-4xl md:text-display-xl font-display font-bold leading-tight',
              textColor,
              'animate-slide-up'
            )}>
              {headline}
            </h1>

            {/* Description */}
            <p className={clsx(
              'text-lg md:text-xl leading-relaxed max-w-2xl',
              descriptionColor,
              'animate-slide-up'
            )}>
              {description}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up">
              <Button
                variant={background === 'subtle' ? 'primary' : 'secondary'}
                size="lg"
                onClick={onPrimaryCta}
              >
                {primaryCta}
              </Button>

              {secondaryCta && (
                <Button
                  variant={background === 'subtle' ? 'outline' : 'ghost'}
                  size="lg"
                  onClick={onSecondaryCta}
                  className={background !== 'subtle' ? 'text-white border-white hover:bg-white/10' : ''}
                >
                  {secondaryCta}
                </Button>
              )}
            </div>

            {/* Optional trust indicators */}
            {background === 'subtle' && (
              <div className="flex items-center gap-6 pt-4 text-sm text-neutral-600">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Non-custodial</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Instant settlements</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Bitcoin rewards</span>
                </div>
              </div>
            )}
          </div>

          {/* Image/Illustration */}
          {image && (
            <div className="relative animate-scale-in">
              {image}
            </div>
          )}

          {/* Default placeholder if no image */}
          {!image && (
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative w-full aspect-square max-w-lg">
                {/* Lightning Network Visualization Placeholder */}
                <div className="absolute inset-0 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <div className="text-6xl">⚡</div>
                    <p className={clsx('text-sm font-medium', textColor)}>
                      Lightning Network
                    </p>
                  </div>
                </div>

                {/* Animated dots */}
                <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-brand-secondary rounded-full animate-pulse" />
                <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-brand-accent rounded-full animate-pulse animation-delay-200" />
                <div className="absolute bottom-1/3 left-1/3 w-3 h-3 bg-brand-primary-light rounded-full animate-pulse animation-delay-400" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

Hero.displayName = 'Hero';

export default Hero;
